/**
 * Recurring-event expansion — pure logic extracted from EventListModule's
 * `expandedEvents` useMemo (PR3). The orchestrator keeps the `useMemo`
 * wrapper; this module owns the deterministic, testable core.
 *
 * The −3mo/+12mo window is derived from an injected `now` so the expansion is
 * deterministic under test. Behavior (occurrence counts, exception override by
 * `recurringEventId`+`originalStartAt`, cancelled/archived skip, virtual-instance
 * markers, 1h default duration, invalid-RRULE fallback) is byte-faithful to the
 * original memo. Diagnostic `console.*` calls are gated behind `DEBUG_RECURRENCE`
 * so they no longer fire on every expansion in production (flip to `true` locally
 * to re-enable the verbose trace).
 */
import { RRule } from 'rrule';

/** Toggle to re-enable the verbose expansion trace during local debugging. */
const DEBUG_RECURRENCE = false;

export interface ExpandRecurringRange {
  rangeStart: Date;
  rangeEnd: Date;
}

/**
 * Compute the default expansion window: 3 months back, 12 months forward from
 * `now`, matching the original `expandedEvents` memo (rangeStart at local
 * midnight, rangeEnd at end-of-day).
 */
export function computeRecurrenceRange(now: Date): ExpandRecurringRange {
  const rangeStart = new Date(now);
  rangeStart.setMonth(rangeStart.getMonth() - 3);
  rangeStart.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(now);
  rangeEnd.setMonth(rangeEnd.getMonth() + 12);
  rangeEnd.setHours(23, 59, 59, 999);
  return { rangeStart, rangeEnd };
}

/**
 * Parse event date strings, handling date-only (YYYY-MM-DD) as UTC midnight
 * and ISO datetime strings normally. Date-only strings must be treated as UTC
 * to avoid timezone shifts that cause the RRULE dtstart to shift, resulting in
 * extra phantom occurrences (issue #3490). Local copy of the dateTimeUtils
 * helper so recurrence.ts stays a leaf with a single dependency (rrule); the
 * orchestrator still uses dateTimeUtils.parseEventDate for everything else.
 */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function parseEventDate(dateStr: unknown): Date | null {
  if (typeof dateStr !== 'string' || !dateStr) return null;
  if (DATE_ONLY_RE.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  }
  const dt = new Date(dateStr);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Expand recurring master events into virtual occurrences within the given
 * range, applying exception overrides and skipping cancelled/archived ones.
 *
 * Byte-faithful port of the EventListModule `expandedEvents` memo body
 * (only the window is parameterized for determinism).
 */
export function expandRecurringEvents(
  events: any[],
  { rangeStart, rangeEnd }: ExpandRecurringRange,
): any[] {
  const masters = events.filter((e: any) => e.rrule && e.rrule.length > 0 && !e.recurringEventId);
  const exceptions = events.filter((e: any) => e.recurringEventId);
  const oneOff = events.filter((e: any) => (!e.rrule || e.rrule.length === 0) && !e.recurringEventId);

  if (DEBUG_RECURRENCE) {
    console.log('[EventList] Recurring expansion:', { masters: masters.length, exceptions: exceptions.length, oneOff: oneOff.length, totalEvents: events.length });
    if (masters.length > 0) {
      console.log('[EventList] Master events:', masters.map((m: any) => ({ id: m.id, title: m.title, rrule: m.rrule, startAt: m.startAt })));
    } else if (events.length > 0) {
      // Log all event fields for the first event to debug whether rrule is present
      const sample = events[0] as any;
      console.log('[EventList] No masters found. Sample event fields:', Object.keys(sample));
      console.log('[EventList] Sample event rrule value:', sample.rrule, 'type:', typeof sample.rrule);
    }
  }

  const expanded: any[] = [...oneOff];

  for (const master of masters) {
    try {
      // Parse the RRULE with dtstart from the master event
      const masterStart = parseEventDate(master.startAt);
      if (!masterStart) {
        if (DEBUG_RECURRENCE) console.warn('[EventList] Could not parse startAt for master', master.id, master.startAt);
        expanded.push(master);
        continue;
      }
      const parsed = RRule.parseString(master.rrule);
      if (DEBUG_RECURRENCE) console.log('[EventList] Parsed RRULE for', master.title, ':', JSON.stringify(parsed));
      const rule = new RRule({
        ...parsed,
        dtstart: masterStart,
      });
      const occurrences = rule.between(rangeStart, rangeEnd, true);
      if (DEBUG_RECURRENCE) console.log('[EventList] Expanded', master.title, ':', occurrences.length, 'occurrences between', rangeStart.toISOString(), 'and', rangeEnd.toISOString());
      const duration = master.endAt
        ? new Date(master.endAt).getTime() - masterStart.getTime()
        : 60 * 60 * 1000; // default 1h

      for (const occ of occurrences) {
        // Check for exception on this occurrence
        const occIso = occ.toISOString();
        const exception = exceptions.find(
          (ex: any) => ex.recurringEventId === master.id &&
            ex.originalStartAt === occIso
        );
        if (exception) {
          if (!exception.cancelledOccurrence && !exception.archived) expanded.push(exception);
          // cancelled/archived exception = deleted occurrence, skip
        } else {
          expanded.push({
            ...master,
            _isVirtual: true,
            _masterEventId: master.id,
            _originalStartAt: occIso,
            startAt: occIso,
            endAt: new Date(occ.getTime() + duration).toISOString(),
          });
        }
      }
    } catch (err) {
      if (DEBUG_RECURRENCE) console.warn('[EventList] Failed to expand RRULE for event', master.id, err);
      expanded.push(master); // fallback: show master as-is
    }
  }

  return expanded;
}
